import { useState, useRef, useEffect, useCallback } from 'react';
import { Video, Square, RotateCcw, Check, Circle, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const MAX_DURATION = 5 * 60; // 5 minutes in seconds

export default function VideoRecorder({ onVideoReady, onCancel, disabled }) {
  const [state, setState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [mimeType, setMimeType] = useState('video/webm');

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    };
  }, [videoPreviewUrl, stopStream]);

  const startCamera = async () => {
    setState('requesting');
    setErrorMessage('');

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('O seu navegador não suporta gravação de vídeo.');
      }
      if (!window.MediaRecorder) {
        throw new Error('O seu navegador não suporta a gravação de vídeo.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          facingMode: 'user',
        },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.error('[VideoRecorder] play() rejected:', playErr);
        }
      }

      setState('previewing');
    } catch (err) {
      console.error('Camera access error:', err);
      stopStream();

      if (err.name === 'NotAllowedError') {
        setErrorMessage('Permissão para aceder à câmara foi recusada. Por favor permita o acesso à câmara nas definições do navegador.');
      } else if (err.name === 'NotFoundError') {
        setErrorMessage('Nenhuma câmara encontrada no dispositivo.');
      } else if (err.name === 'NotReadableError') {
        setErrorMessage('A câmara está a ser utilizada por outra aplicação.');
      } else {
        setErrorMessage(err.message || 'Erro ao aceder à câmara.');
      }
      setState('error');
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    setRecordingTime(0);

    let selectedMime = 'video/webm';
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
      selectedMime = 'video/webm;codecs=vp9,opus';
    } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
      selectedMime = 'video/webm;codecs=vp8,opus';
    } else if (MediaRecorder.isTypeSupported('video/webm')) {
      selectedMime = 'video/webm';
    } else if (MediaRecorder.isTypeSupported('video/mp4')) {
      selectedMime = 'video/mp4';
    }

    setMimeType(selectedMime);

    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: selectedMime,
      videoBitsPerSecond: 1500000,
    });

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: selectedMime });
      setVideoBlob(blob);
      const url = URL.createObjectURL(blob);
      setVideoPreviewUrl(url);

      // Switch the SAME video element from live stream to recorded blob
      const el = videoRef.current;
      if (el) {
        el.srcObject = null;
        el.src = url;
        el.muted = false;
        el.load();
        // Don't autoplay - let user decide when to play
      }

      stopStream();
      setState('recorded');
    };

    recorder.start(1000);
    recorderRef.current = recorder;

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const next = prev + 1;
        if (next >= MAX_DURATION) {
          stopRecording();
        }
        return next;
      });
    }, 1000);

    setState('recording');
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const handleReRecord = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoBlob(null);
    setVideoPreviewUrl(null);
    setRecordingTime(0);
    setState('idle');
  };

  const handleConfirmVideo = async () => {
    if (!videoBlob) return;

    setState('uploading');
    setUploadProgress(0);

    try {
      const contentType = mimeType.startsWith('video/mp4') ? 'video/mp4' : 'video/webm';

      const urlResponse = await fetch(`${API}/upload/video-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: contentType }),
      });

      if (!urlResponse.ok) {
        const err = await urlResponse.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao obter URL de upload');
      }

      const { upload_url, public_url } = await urlResponse.json();

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url, true);
        xhr.setRequestHeader('Content-Type', contentType);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload falhou com status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Falha no upload'));
        xhr.send(videoBlob);
      });

      setState('uploaded');
      onVideoReady(public_url);
      toast.success('Vídeo carregado com sucesso!');

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao carregar vídeo. Por favor tente novamente.');
      setState('recorded');
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const remainingTime = MAX_DURATION - recordingTime;
  const isRecorded = state === 'recorded';
  const isRecording = state === 'recording';
  // Single video element persists across requesting, previewing, recording, AND recorded
  const showVideo = state === 'requesting' || state === 'previewing' || isRecording || isRecorded;

  // ---- RENDER ----

  if (state === 'error') {
    return (
      <div className="border-2 border-red-200 bg-red-50 p-6 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-sm text-red-600 mb-4">{errorMessage}</p>
        <div className="flex gap-3 justify-center">
          <Button
            onClick={() => { setState('idle'); setErrorMessage(''); }}
            variant="outline"
            className="text-sm"
          >
            Tentar novamente
          </Button>
          {onCancel && (
            <Button onClick={onCancel} variant="outline" className="text-sm">
              Escrever texto
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (state === 'idle') {
    return (
      <div className="border-2 border-dashed border-gray-300 p-8 text-center">
        <Video className="w-12 h-12 text-[#B5985A] mx-auto mb-3" />
        <p className="text-gray-600 mb-1 text-sm">
          Grave um vídeo com a sua opinião
        </p>
        <p className="text-gray-400 mb-4 text-xs">
          Máximo 5 minutos &middot; 720p
        </p>
        <Button
          onClick={startCamera}
          className="btn-gold"
          disabled={disabled}
        >
          <Video className="w-4 h-4 mr-2" />
          Iniciar Câmara
        </Button>
      </div>
    );
  }

  // Single persistent video element for requesting → previewing → recording → recorded
  if (showVideo) {
    return (
      <div className={`border-2 ${isRecording ? 'border-red-300' : isRecorded ? 'border-[#B5985A]' : 'border-gray-200'} overflow-hidden`}>
        <div className="relative bg-black aspect-video">
          <video
            ref={videoRef}
            autoPlay={!isRecorded}
            playsInline
            muted={!isRecorded}
            controls={isRecorded}
            className={`w-full h-full ${isRecorded ? 'object-contain' : 'object-cover mirror-video'}`}
          />

          {/* Loading overlay while requesting camera */}
          {state === 'requesting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
              <Loader2 className="w-8 h-8 text-[#B5985A] mb-3 animate-spin" />
              <p className="text-white text-sm">A aceder à câmara...</p>
              <p className="text-gray-400 text-xs mt-2">
                Por favor permita o acesso quando solicitado
              </p>
            </div>
          )}

          {/* Recording indicator overlay */}
          {isRecording && (
            <>
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full">
                <div className="w-3 h-3 bg-red-500 rounded-full recording-pulse" />
                <span className="text-white text-sm font-mono">{formatTime(recordingTime)}</span>
              </div>
              {remainingTime <= 30 && (
                <div className="absolute top-3 right-3 bg-red-500/80 text-white px-3 py-1.5 rounded-full text-xs">
                  {formatTime(remainingTime)} restantes
                </div>
              )}
            </>
          )}
        </div>

        {/* Previewing controls */}
        {state === 'previewing' && (
          <div className="p-4 flex justify-center gap-3">
            <Button
              onClick={() => { stopStream(); setState('idle'); }}
              variant="outline"
              className="text-sm"
            >
              Cancelar
            </Button>
            <Button
              onClick={startRecording}
              className="bg-red-500 hover:bg-red-600 text-white text-sm flex items-center gap-2"
            >
              <Circle className="w-4 h-4 fill-current" />
              Gravar
            </Button>
          </div>
        )}

        {/* Recording controls */}
        {isRecording && (
          <div className="p-4 flex justify-center">
            <Button
              onClick={stopRecording}
              className="bg-red-500 hover:bg-red-600 text-white text-sm flex items-center gap-2"
            >
              <Square className="w-4 h-4 fill-current" />
              Parar Gravação
            </Button>
          </div>
        )}

        {/* Recorded controls */}
        {isRecorded && (
          <div className="p-4">
            <p className="text-center text-sm text-gray-500 mb-3">
              Duração: {formatTime(recordingTime)}
            </p>
            <div className="flex justify-center gap-3">
              <Button
                onClick={handleReRecord}
                variant="outline"
                className="text-sm flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Regravar
              </Button>
              <Button
                onClick={handleConfirmVideo}
                className="btn-gold text-sm flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Submeter
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (state === 'uploading') {
    return (
      <div className="border-2 border-[#B5985A] p-6 text-center">
        <Loader2 className="w-8 h-8 text-[#B5985A] mx-auto mb-3 animate-spin" />
        <p className="text-gray-600 text-sm mb-3">A carregar vídeo...</p>
        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden max-w-xs mx-auto">
          <div
            className="h-full bg-[#B5985A] transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-2">{uploadProgress}%</p>
      </div>
    );
  }

  if (state === 'uploaded') {
    return (
      <div className="border-2 border-green-300 bg-green-50 p-6 text-center">
        <Check className="w-10 h-10 text-green-500 mx-auto mb-3" />
        <p className="text-green-700 text-sm font-medium">
          Vídeo carregado com sucesso!
        </p>
      </div>
    );
  }

  return null;
}
